import React, {useEffect, useState} from "react";
import {Alert, Box, Button, Checkbox, Drawer, FormControlLabel, Grid, IconButton, TextField, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TextFieldWithDirtyValidation from "../../form/TextFieldWithDirtyValidation";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import {UserRoleToTypeName, UserTypeDBFormat} from "../../constants/securspace-constants";
import PropTypes from "prop-types";
import PhoneInput from "react-phone-number-input/input";
import {PhoneNumberTextField} from "../../form/PhoneNumberTextField";
import UserLocationSelect from "./UserLocationSelect";
import {isSupplierAccount} from "../../../util/AccountUtils";
import {styled} from "@mui/material/styles";

const CreateEditUserAppInput = styled(FormControl)(() => ({
  width: '100%',
}));

// Field text/box sizing + spacing mirrored from the Stripe card form (StripeCardForm.tsx)
// for a consistent, compact look across the side-panel forms.
const INPUT_SX = {
  '& .MuiInputBase-input': { fontSize: '1em', padding: '0.65em 0.75em' },
  '& .MuiInputLabel-root': { fontSize: '0.86em' },
};

const CreateEditUserHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const CreateEditUserBody = styled(Box)(({theme}) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(0, 3, 2),
}));

const CreateEditUserFooter = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.grey["100"],
}));

const CreateEditUserRowGap10 = styled(Grid)(() => ({
  marginTop: '1rem',
}));

const CreateEditUser = ({
  open,
  onClose,
  onSubmit,
  onTextChange,
  onSelectChange,
  onPhoneNumberChange,
  isEdit,
  isBuyer,
  user,
  emailErrorMsg,
  firstNameErrorMsg,
  lastNameErrorMsg,
  phoneNumberErrorMsg,
  roleErrorMsg,
  roleBasedOptions,
  formErrorMsg,
  adminUsersArray,
  account,
  handleLocationIdsChange,
  onSmsOptInChange
}) => {
  const [userIsAdminOrOwner, setUserIsAdminOrOwner] = useState(false);
  const [selectComponentLabel, setSelectComponentLabel] = useState("");

  useEffect(() => {
    // Compare by a stable key: UserManagement passes a cloned user object, so reference
    // equality (adminUsersArray.includes(user)) would never match.
    const isAdminOrOwner = adminUsersArray.some(adminUser => adminUser.username === user.username);
    if (isAdminOrOwner) {
      setUserIsAdminOrOwner(true);
      setSelectComponentLabel("Cannot change role of Admin or Owner");
    } else {
      setUserIsAdminOrOwner(false);
      setSelectComponentLabel("Select Role");
    }
  }, [user, adminUsersArray]);

  return (
    <Drawer
      anchor={'right'}
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, maxWidth: '100%', display: 'flex', flexDirection: 'column' } }}
    >
      <CreateEditUserHeader>
        <Typography variant={'h6'} component={'h2'} id={'ss-create-edit-user'}>{isEdit ? 'Edit User' : 'Add User'}</Typography>
        <IconButton aria-label={'Close'} onClick={onClose} edge={'end'}>
          <CloseIcon />
        </IconButton>
      </CreateEditUserHeader>
      <CreateEditUserBody>
        <form id={"ss-create-edit-user-form"} onSubmit={onSubmit} noValidate className={"w-100"}>
          <CreateEditUserRowGap10 container direction={'column'}>
            {
              isEdit ?
                <TextField
                  value={user.username}
                  name={"email"}
                  label={"Email"}
                  placeholder={"Email"}
                  type={"email"}
                  size={"small"}
                  fullWidth
                  disabled
                  sx={{ mb: '1em', ...INPUT_SX }}
                />
                :
                <TextFieldWithDirtyValidation
                  value={user.username}
                  onChange={onTextChange}
                  name={"email"}
                  label={"Email"}
                  placeholder={"Email"}
                  type={"email"}
                  variant={"outlined"}
                  size={"small"}
                  error={!!emailErrorMsg}
                  helperText={emailErrorMsg}
                  fullWidth
                  required
                  sx={{ mb: '1em', ...INPUT_SX }}
                />
            }
            <Grid container spacing={2} sx={{ mb: '1em' }}>
              <Grid item xs={12} sm={6}>
                <TextFieldWithDirtyValidation
                  value={user.firstName}
                  onChange={onTextChange}
                  name="firstName"
                  label="First Name"
                  placeholder="Enter your first name"
                  variant="outlined"
                  size="small"
                  error={!!firstNameErrorMsg}
                  helperText={firstNameErrorMsg}
                  fullWidth
                  required
                  sx={{ mb: 0, ...INPUT_SX }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldWithDirtyValidation
                  value={user.lastName}
                  onChange={onTextChange}
                  name="lastName"
                  label="Last Name"
                  placeholder="Enter your last name"
                  variant="outlined"
                  size="small"
                  error={!!lastNameErrorMsg}
                  helperText={lastNameErrorMsg}
                  fullWidth
                  required
                  sx={{ mb: 0, ...INPUT_SX }}
                />
              </Grid>
            </Grid>
            <PhoneInput
              value={user.phoneNumber}
              onChange={onPhoneNumberChange}
              inputComponent={PhoneNumberTextField}
              defaultCountry={"US"}
              size={"small"}
              error={!!phoneNumberErrorMsg}
              helperText={phoneNumberErrorMsg}
              fullWidth
              sx={{ mb: '1em', ...INPUT_SX }}
            />
            {isBuyer && (
              <Box sx={{ mb: '1.5em' }}>
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', m: 0 }}
                  control={
                    <Checkbox
                      checked={!!user.isOptedIntoText}
                      onChange={onSmsOptInChange}
                      name="isOptedIntoText"
                      disabled={!user.phoneNumber}
                      sx={{ pt: 0 }}
                    />
                  }
                  label={
                    <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                      Send this user text message notifications about bookings, payments, and account updates.
                    </Typography>
                  }
                />
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    Msg &amp; data rates may apply. Msg frequency varies. The user will receive a text message if it is a valid mobile number.
                  </Typography>
                </Alert>
              </Box>
            )}
            {user?.role !== UserTypeDBFormat.ROLE_USERTYPE_PRIMARY_OWNER && (
              userIsAdminOrOwner ? (
                <TextField
                  label={selectComponentLabel}
                  value={UserRoleToTypeName[user.role] || UserRoleToTypeName[UserTypeDBFormat.ROLE_USERTYPE_OWNER]}
                  size="small"
                  fullWidth
                  disabled
                  required
                  sx={{ mb: '1em', ...INPUT_SX }}
                />
              ) : (
                <CreateEditUserAppInput variant="outlined" size="small" required error={!!roleErrorMsg} sx={{ mb: '1em', ...INPUT_SX }}>
                  <InputLabel id={"create-edit-user-role-label"}>{selectComponentLabel}</InputLabel>
                  <Select
                    labelId={"create-edit-user-role-label"}
                    id={"create-edit-user-select-role"}
                    label={selectComponentLabel}
                    value={user.role}
                    onChange={onSelectChange}
                  >
                    {
                      roleBasedOptions.map(role => (
                        <MenuItem value={role} key={role}>
                          {UserRoleToTypeName[role]}
                        </MenuItem>
                      ))
                    }
                  </Select>
                  {roleErrorMsg && <FormHelperText>{roleErrorMsg}</FormHelperText>}
                </CreateEditUserAppInput>
              )
            )}
            {
              isSupplierAccount(account) &&
              <UserLocationSelect
                selectedLocationIds={user.locationIds}
                handleLocationIdsChange={handleLocationIdsChange}
              />
            }
            {
              formErrorMsg &&
              <Typography variant="body1" component="div" color="error" align="center">{formErrorMsg}</Typography>
            }
          </CreateEditUserRowGap10>
        </form>
      </CreateEditUserBody>
      <CreateEditUserFooter>
        <Button color={"primary"} onClick={onClose}>Cancel</Button>
        <Button variant={"contained"} color={"primary"} type={"submit"} form={"ss-create-edit-user-form"}>{isEdit ? 'Update' : 'Add'}</Button>
      </CreateEditUserFooter>
    </Drawer>
  );
};

CreateEditUser.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onTextChange: PropTypes.func.isRequired,
  onPhoneNumberChange: PropTypes.func.isRequired,
  onSelectChange: PropTypes.func.isRequired,
  onSmsOptInChange: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isBuyer: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  emailErrorMsg: PropTypes.string,
  firstNameErrorMsg: PropTypes.string,
  lastNameErrorMsg: PropTypes.string,
  roleErrorMsg: PropTypes.string,
  roleBasedOptions: PropTypes.arrayOf(
    (propValue, key, componentName, location, propFullName) => {
      if (!UserTypeDBFormat[propValue[key]]) {
        return new Error(
          `Invalid prop \`${propFullName}\` supplied to` +
          ` \`${componentName}\`. Prop must be of type \`${Object.keys(UserTypeDBFormat).join('|| ')}\``
        );
      }
    }).isRequired,
  formErrorMsg: PropTypes.string,
  adminUsersArray: PropTypes.array.isRequired,
  account: PropTypes.object.isRequired,
  handleLocationIdsChange: PropTypes.func.isRequired,
};

export default CreateEditUser;
